import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Lock, Unlock, KeyRound, Plus, Edit2, Trash2, FileDown, X, RefreshCw, Search, ShieldCheck,
  Receipt, ExternalLink, Upload, FileText, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";

const CATEGORIES = [
  {
    id: "FIXED_EXPENSES", label: "Fixed Expenses", short: "Fixed",
    accent: "bg-[#1A4DFF]", tint: "bg-[#E7EEFF]",
    presets: ["AI Software", "Marketing (Influencer)", "Rent", "Salary", "Light Bill", "Shopify", "Wifi", "Flexipe", "Office Expenses"],
  },
  {
    id: "PRODUCT_PURCHASE", label: "Product Purchase", short: "Product",
    accent: "bg-[#FF7A00]", tint: "bg-[#FFE5CC]",
    presets: ["T-Shirt", "Tags", "Bags", "Prints (DTF)", "Courier", "Poster", "Meta Ads"],
  },
  {
    id: "ONLINE_COLLECTIONS", label: "Online Collections", short: "Collections",
    accent: "bg-[#22A06B]", tint: "bg-[#DFF3E8]",
    presets: ["RazorPay", "Easybuzz", "Delivery"],
  },
  {
    id: "MISC_EXPENSES", label: "Miscellaneous Expenses", short: "Misc",
    accent: "bg-[#7A00E6]", tint: "bg-[#EEE0FF]",
    presets: ["Travel", "Food", "Repairs", "Gift", "Other"],
  },
  {
    id: "FUNDS_FROM_PAPA", label: "Funds from Papa", short: "Papa Fund",
    accent: "bg-[#D4A017]", tint: "bg-[#FFF3CC]", inflow: true,
    presets: ["Transfer", "Cash", "Cheque", "UPI"],
  },
];

const FINANCE_TOKEN_KEY = "closet_finance_token";
const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};

export default function Finance() {
  const [token, setToken] = useState(() => sessionStorage.getItem(FINANCE_TOKEN_KEY) || "");

  if (!token) return <PinGate onUnlock={(t) => { sessionStorage.setItem(FINANCE_TOKEN_KEY, t); setToken(t); }} />;

  return <FinanceApp token={token} onLock={() => { sessionStorage.removeItem(FINANCE_TOKEN_KEY); setToken(""); }} />;
}

function PinGate({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(false);

  async function submit(e) {
    e?.preventDefault();
    if (pin.length < 4) { toast.error("PIN must be at least 4 digits"); return; }
    setBusy(true);
    try {
      const res = await api.post("/finance/unlock", { pin });
      onUnlock(res.data.token);
      toast.success("Finance vault unlocked");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setPin("");
    } finally { setBusy(false); }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10 min-h-screen flex items-center justify-center" data-testid="finance-pin-gate">
      <div className="brut-card p-10 max-w-md w-full bg-white">
        <div className="mb-6 flex items-center gap-3">
          <div className="w-14 h-14 bg-black text-white grid place-items-center border-2 border-black shadow-[4px_4px_0_0_#0a0a0a]">
            <Lock size={24} />
          </div>
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">/ VAULT · FINANCE</div>
            <h1 className="font-display font-black text-3xl">Enter PIN</h1>
          </div>
        </div>
        <p className="text-sm text-zinc-600 mb-6">This section is separately locked. Enter your 4-digit finance PIN to view expenses and collections.</p>
        <form onSubmit={submit}>
          <div className="relative mb-4">
            <input
              type={visible ? "text" : "password"}
              inputMode="numeric"
              pattern="\d*"
              maxLength={8}
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="••••"
              data-testid="finance-pin-input"
              className="brut-input text-center font-mono-ds font-black text-4xl tracking-[0.5em] py-4 pr-14"
            />
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 border-2 border-[#2F343A] bg-white hover:bg-zinc-100"
              aria-label={visible ? "Hide PIN" : "Show PIN"}
              data-testid="finance-pin-input-toggle"
            >
              {visible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <button type="submit" disabled={busy || pin.length < 4}
            data-testid="finance-unlock-btn"
            className="brut-btn w-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Unlock size={16} /> {busy ? "Unlocking…" : "Unlock Vault"}
          </button>
        </form>
        <div className="mt-6 font-mono-ds text-[10px] tracking-widest uppercase text-zinc-400 text-center">
          Session lasts 8 hours · Locks on page refresh
        </div>
      </div>
    </div>
  );
}

function makeFinanceApi(token) {
  return {
    get: (url, opts = {}) => api.get(url, { ...opts, headers: { ...(opts.headers || {}), "X-Finance-Token": token } }),
    post: (url, data, opts = {}) => api.post(url, data, { ...opts, headers: { ...(opts.headers || {}), "X-Finance-Token": token } }),
    put: (url, data, opts = {}) => api.put(url, data, { ...opts, headers: { ...(opts.headers || {}), "X-Finance-Token": token } }),
    delete: (url, opts = {}) => api.delete(url, { ...opts, headers: { ...(opts.headers || {}), "X-Finance-Token": token } }),
  };
}

function FinanceApp({ token, onLock }) {
  const fapi = useMemo(() => makeFinanceApi(token), [token]);
  const [cat, setCat] = useState(CATEGORIES[0].id);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ by_category: {}, total: 0 });
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState("");     // YYYY-MM
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);

  async function load() {
    if (cat === "BILLS") {
      // just refresh stats when viewing bills
      try {
        const b = await fapi.get("/finance/stats", { params: month ? { month } : {} });
        setStats(b.data);
      } catch (e) {
        if (e.response?.status === 401) { toast.error("Finance session expired · re-enter PIN"); onLock(); }
      }
      return;
    }
    try {
      const params = { category: cat };
      if (month) params.month = month;
      const [a, b] = await Promise.all([
        fapi.get("/finance", { params }),
        fapi.get("/finance/stats", { params: month ? { month } : {} }),
      ]);
      setItems(a.data);
      setStats(b.data);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 401) {
        toast.error("Finance session expired · re-enter PIN");
        onLock();
        return;
      }
      toast.error(formatApiErrorDetail(detail) || e.message);
    }
  }
  useEffect(() => { load(); }, [cat, month]);

  const catMeta = CATEGORIES.find((c) => c.id === cat) || { label: "Bills", short: "Bills", accent: "bg-[#0EA5E9]", tint: "bg-[#DFF4FE]" };
  const visible = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((it) =>
      (it.subcategory || "").toLowerCase().includes(q) ||
      (it.notes || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  async function onDelete(it) {
    if (!window.confirm(`Delete "${it.subcategory}" (₹${it.amount})?`)) return;
    try { await fapi.delete(`/finance/${it.id}`); toast.success("Entry deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  function downloadExcel(category) {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (month) params.set("month", month);
    params.set("finance_token", token);
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const url = `${BACKEND}/api/exports/finance.xlsx?${params.toString()}`;
    // Preserve session cookie via new-tab; browser will send access_token cookie automatically
    window.open(url, "_blank");
  }

  const total = stats.total || 0;

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="finance-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2 inline-flex items-center gap-2">
            <ShieldCheck size={12} /> / VAULT · FINANCE
          </div>
          <h1 className="section-title text-5xl lg:text-6xl">Finance.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">Track fixed expenses, product purchases and online collections. All figures private — PIN-gated.</p>
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          <button onClick={() => { setEditing(null); setOpen(true); }} className="brut-btn px-5 py-3 inline-flex items-center gap-2" data-testid="add-finance-btn">
            <Plus size={16} /> New Entry
          </button>
          <button onClick={() => downloadExcel(cat)} className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2" data-testid="download-finance-current-btn">
            <FileDown size={14} /> {catMeta.short} Excel
          </button>
          <button onClick={() => downloadExcel("")} className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2" data-testid="download-finance-all-btn">
            <FileDown size={14} /> All Excel
          </button>
          <button onClick={() => setPinOpen(true)} className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2" data-testid="change-pin-btn">
            <KeyRound size={14} /> Change PIN
          </button>
          <button onClick={onLock} className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2" data-testid="finance-lock-btn">
            <Lock size={14} /> Lock
          </button>
        </div>
      </header>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {CATEGORIES.map((c) => {
          const active = cat === c.id;
          const catStat = stats.by_category?.[c.id] || { amount: 0, count: 0 };
          return (
            <button key={c.id} onClick={() => setCat(c.id)} data-testid={`finance-tab-${c.id}`}
              className={`px-5 py-3 border-2 border-black font-display font-black text-sm uppercase tracking-wider transition-all ${
                active ? `${c.accent} text-white shadow-[4px_4px_0_0_#0a0a0a]` : "bg-white text-black hover:bg-zinc-100"
              }`}>
              {c.label}
              <span className={`ml-2 font-mono-ds text-[10px] ${active ? "opacity-90" : "opacity-60"}`}>
                ({catStat.count} · ₹{Math.round(catStat.amount).toLocaleString("en-IN")})
              </span>
            </button>
          );
        })}
        <button onClick={() => setCat("BILLS")} data-testid="finance-tab-BILLS"
          className={`px-5 py-3 border-2 border-black font-display font-black text-sm uppercase tracking-wider transition-all inline-flex items-center gap-2 ${
            cat === "BILLS" ? "bg-[#0EA5E9] text-white shadow-[4px_4px_0_0_#0a0a0a]" : "bg-white text-black hover:bg-zinc-100"
          }`}>
          <Receipt size={14} /> Bills / Invoices
        </button>
      </div>

      {/* Pie chart */}
      <FinancePie stats={stats.by_category || {}} />

      {cat === "BILLS" ? (
        <BillsPanel token={token} fapi={fapi} />
      ) : (
      <>
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {(() => {
          const bc = stats.by_category || {};
          const inflow = Number(bc.FUNDS_FROM_PAPA?.amount || 0) + Number(bc.ONLINE_COLLECTIONS?.amount || 0);
          const outflow =
            Number(bc.FIXED_EXPENSES?.amount || 0) +
            Number(bc.PRODUCT_PURCHASE?.amount || 0) +
            Number(bc.MISC_EXPENSES?.amount || 0);
          const net = inflow - outflow;
          return (
            <>
              <Stat label="Money In (Coll. + Papa)" value={`₹${Math.round(inflow).toLocaleString("en-IN")}`} accent />
              <Stat label="Money Out (Expenses)" value={`₹${Math.round(outflow).toLocaleString("en-IN")}`} />
              <Stat label={`${catMeta.short} Total`} value={`₹${Math.round(stats.by_category?.[cat]?.amount || 0).toLocaleString("en-IN")}`} />
              <Stat label="Net (In − Out)" value={`${net < 0 ? "−" : ""}₹${Math.round(Math.abs(net)).toLocaleString("en-IN")}`} accent />
            </>
          );
        })()}
      </div>

      {/* Filter strip */}
      <div className="brut-card p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sub-category / notes…" className="brut-input pl-10" data-testid="finance-search" />
        </div>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="brut-input font-mono-ds max-w-[180px]" data-testid="finance-month-filter" />
        {month && (
          <button onClick={() => setMonth("")} className="brut-btn-outline px-3 py-2" data-testid="finance-clear-month">Clear month</button>
        )}
        <button onClick={load} className="brut-btn-outline px-3 py-2 inline-flex items-center gap-2 ml-auto" data-testid="finance-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="brut-card overflow-x-auto">
        <table className="brut-table min-w-[900px]">
          <thead>
            <tr>
              <th>Date</th>
              <th>Sub-category</th>
              <th>Amount</th>
              <th>Notes</th>
              <th>User</th>
              <th className="w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((it) => (
              <tr key={it.id} className={catMeta.tint} data-testid={`finance-row-${it.id}`}>
                <td className="font-mono-ds text-xs whitespace-nowrap">{it.date}</td>
                <td className="font-display font-bold">{it.subcategory || "—"}</td>
                <td className="font-mono-ds font-black">₹{Math.round(it.amount).toLocaleString("en-IN")}</td>
                <td className="text-xs max-w-[300px] truncate" title={it.notes}>{it.notes || "—"}</td>
                <td className="font-mono-ds text-xs">{it.user_name || "—"}</td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(it); setOpen(true); }} className="p-1.5 border-2 border-black hover:bg-zinc-100" data-testid={`edit-finance-${it.id}`}><Edit2 size={12} /></button>
                    <button onClick={() => onDelete(it)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" data-testid={`delete-finance-${it.id}`}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-zinc-500 font-mono-ds text-xs uppercase">No entries · Click &quot;New Entry&quot; to add</td></tr>
            )}
          </tbody>
          {visible.length > 0 && (
            <tfoot>
              <tr className="bg-black text-white font-display font-black">
                <td colSpan={2} className="text-right pr-4 font-mono-ds text-[11px] tracking-widest uppercase">TOTAL →</td>
                <td className="font-mono-ds font-black text-base">₹{Math.round(visible.reduce((a, it) => a + (Number(it.amount) || 0), 0)).toLocaleString("en-IN")}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {open && <EntryForm entry={editing} category={cat} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} fapi={fapi} />}
      {pinOpen && <ChangePinModal onClose={() => setPinOpen(false)} fapi={fapi} />}
      </>
      )}
    </div>
  );
}

function FinancePie({ stats }) {
  const data = useMemo(() => {
    const meta = {
      FIXED_EXPENSES: { name: "Fixed Expenses", fill: "#1A4DFF", kind: "out" },
      PRODUCT_PURCHASE: { name: "Product Purchase", fill: "#FF7A00", kind: "out" },
      ONLINE_COLLECTIONS: { name: "Online Collections", fill: "#22A06B", kind: "in" },
      MISC_EXPENSES: { name: "Miscellaneous", fill: "#7A00E6", kind: "out" },
      FUNDS_FROM_PAPA: { name: "Funds from Papa", fill: "#D4A017", kind: "in" },
    };
    return Object.entries(meta)
      .map(([k, m]) => ({ key: k, name: m.name, fill: m.fill, kind: m.kind, value: Math.round(Number(stats[k]?.amount || 0)) }))
      .filter((d) => d.value > 0);
  }, [stats]);
  const total = data.reduce((a, d) => a + d.value, 0);
  const inflow = data.filter((d) => d.kind === "in").reduce((a, d) => a + d.value, 0);
  const outflow = data.filter((d) => d.kind === "out").reduce((a, d) => a + d.value, 0);

  if (total === 0) return null;

  return (
    <div className="brut-card p-5 mb-4" data-testid="finance-pie">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Category Split</div>
          <div className="font-display font-black text-xl">Where your money lives</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 font-mono-ds text-xs">
          <div><span className="opacity-60 uppercase">In</span> <span className="font-black">₹{inflow.toLocaleString("en-IN")}</span></div>
          <div><span className="opacity-60 uppercase">Out</span> <span className="font-black">₹{outflow.toLocaleString("en-IN")}</span></div>
          <div className="bg-black text-white px-3 py-1"><span className="opacity-70 uppercase">Net</span> <span className="font-black">{inflow - outflow < 0 ? "−" : ""}₹{Math.abs(inflow - outflow).toLocaleString("en-IN")}</span></div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="35%" cy="50%" outerRadius={100} innerRadius={55} stroke="#0a0a0a" strokeWidth={2} paddingAngle={2}>
            {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
          </Pie>
          <Tooltip content={<PieTip total={total} />} />
          <Legend
            layout="vertical" verticalAlign="middle" align="right"
            wrapperStyle={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}
            formatter={(value) => {
              const d = data.find((x) => x.name === value);
              if (!d) return value;
              const pct = ((d.value / total) * 100).toFixed(1);
              return `${value} · ₹${d.value.toLocaleString("en-IN")} (${pct}%)`;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function PieTip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : "0";
  return (
    <div className="bg-black text-white p-3 border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,0.4)]">
      <div className="font-display font-black text-sm mb-1">{d.name}</div>
      <div className="font-mono-ds text-[10px]">₹{d.value.toLocaleString("en-IN")} · {pct}%</div>
    </div>
  );
}

function BillsPanel({ token, fapi }) {
  const [bills, setBills] = useState([]);
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // bill being edited, or null
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  async function load() {
    try {
      const params = {};
      if (search) params.search = search;
      if (month) params.month = month;
      const res = await fapi.get("/finance/bills", { params });
      setBills(res.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  useEffect(() => { load(); }, [search, month]);

  async function onDelete(b) {
    if (!window.confirm(`Delete bill "${b.vendor}" (₹${b.amount})?`)) return;
    try { await fapi.delete(`/finance/bills/${b.id}`); toast.success("Bill deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  const totalAmount = bills.reduce((a, b) => a + (Number(b.amount) || 0), 0);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Stat label="Bills Uploaded" value={bills.length} />
        <Stat label="Total Amount" value={`₹${Math.round(totalAmount).toLocaleString("en-IN")}`} accent />
        <Stat label="Unique Vendors" value={new Set(bills.map((b) => b.vendor)).size} />
      </div>

      <div className="brut-card p-4 mb-4 flex flex-wrap items-center gap-3">
        <button onClick={() => setOpen(true)} className="brut-btn px-4 py-2 inline-flex items-center gap-2" data-testid="upload-bill-btn">
          <Upload size={14} /> Upload Bill
        </button>
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendor / notes…" className="brut-input pl-10" data-testid="bills-search" />
        </div>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="brut-input font-mono-ds max-w-[180px]" data-testid="bills-month-filter" />
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="brut-table min-w-[900px]">
          <thead>
            <tr>
              <th>Date</th><th>Vendor</th><th>Amount</th><th>File</th><th>Notes</th><th>Uploaded by</th>
              <th className="w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bills.map((b) => {
              const fileUrl = `${BACKEND}/api/finance/bills/${b.id}/file?finance_token=${encodeURIComponent(token)}`;
              const isImg = (b.content_type || "").startsWith("image/");
              return (
                <tr key={b.id} data-testid={`bill-row-${b.id}`}>
                  <td className="font-mono-ds text-xs whitespace-nowrap">{b.date}</td>
                  <td className="font-display font-bold">{b.vendor}</td>
                  <td className="font-mono-ds font-black">₹{Math.round(b.amount).toLocaleString("en-IN")}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      {isImg ? (
                        <a href={fileUrl} target="_blank" rel="noreferrer">
                          <img src={fileUrl} alt="" className="w-10 h-10 border-2 border-black object-cover" />
                        </a>
                      ) : (
                        <div className="w-10 h-10 border-2 border-black bg-zinc-100 flex items-center justify-center font-mono-ds text-[10px]">
                          {(b.file_name || "").split(".").pop().toUpperCase().slice(0, 4)}
                        </div>
                      )}
                      <a href={fileUrl} target="_blank" rel="noreferrer" className="text-xs font-mono-ds underline inline-flex items-center gap-1" data-testid={`bill-open-${b.id}`}>
                        Open <ExternalLink size={11} />
                      </a>
                    </div>
                  </td>
                  <td className="text-xs max-w-[260px] truncate" title={b.notes}>{b.notes || "—"}</td>
                  <td className="font-mono-ds text-xs">{b.user_name || "—"}</td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(b)} className="p-1.5 border-2 border-black hover:bg-zinc-100" title="Edit metadata" data-testid={`bill-edit-${b.id}`}><Edit2 size={12} /></button>
                      <button onClick={() => onDelete(b)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" data-testid={`bill-delete-${b.id}`}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {bills.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-zinc-500 font-mono-ds text-xs uppercase">
                No bills uploaded yet · Click &quot;Upload Bill&quot; to add
              </td></tr>
            )}
          </tbody>
          {bills.length > 0 && (
            <tfoot>
              <tr className="bg-black text-white font-display font-black">
                <td colSpan={2} className="text-right pr-4 font-mono-ds text-[11px] uppercase tracking-widest">TOTAL →</td>
                <td className="font-mono-ds font-black text-base">₹{Math.round(totalAmount).toLocaleString("en-IN")}</td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {open && <UploadBillModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} fapi={fapi} />}
      {editing && <EditBillModal bill={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} fapi={fapi} />}
    </>
  );
}

function EditBillModal({ bill, onClose, onSaved, fapi }) {
  const [form, setForm] = useState({
    vendor: bill.vendor || "",
    amount: bill.amount ?? "",
    date: bill.date || todayIST(),
    notes: bill.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!form.vendor.trim()) { toast.error("Vendor is required"); return; }
    if (form.amount === "" || Number(form.amount) < 0) { toast.error("Amount is required"); return; }
    setBusy(true);
    try {
      await fapi.put(`/finance/bills/${bill.id}`, {
        vendor: form.vendor.trim(),
        amount: Number(form.amount) || 0,
        date: form.date,
        notes: form.notes,
      });
      toast.success("Bill updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="edit-bill-modal">
      <div className="brut-card w-full max-w-lg bg-white">
        <div className="flex items-center justify-between p-5 border-b-2 border-black bg-[#0EA5E9] text-white">
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-80">EDIT · BILL</div>
            <h2 className="font-display font-black text-2xl">Edit Bill Details</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/20" data-testid="close-edit-bill"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="brut-card-flat p-3 bg-[#FDF6EE] font-mono-ds text-[10px] uppercase tracking-widest text-zinc-600">
            File cannot be changed here. To replace, delete this bill and upload again.
          </div>
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Vendor *</div>
            <input className="brut-input" value={form.vendor} onChange={(e) => set("vendor", e.target.value)} data-testid="edit-bill-vendor-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Amount (₹) *</div>
              <input type="number" min={0} step="0.01" className="brut-input font-mono-ds font-bold text-lg" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="edit-bill-amount-input" />
            </div>
            <div>
              <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Date</div>
              <input type="date" className="brut-input font-mono-ds" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="edit-bill-date-input" />
            </div>
          </div>
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Notes</div>
            <textarea rows={2} className="brut-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="edit-bill-notes-input" />
          </div>
        </div>
        <div className="border-t-2 border-black p-5 flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-5 py-2.5">Cancel</button>
          <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5 disabled:opacity-60" data-testid="save-edit-bill-btn">
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadBillModal({ onClose, onSaved, fapi }) {
  const [form, setForm] = useState({ vendor: "", amount: "", date: todayIST(), notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!form.vendor.trim()) { toast.error("Vendor is required"); return; }
    if (form.amount === "" || Number(form.amount) < 0) { toast.error("Amount is required"); return; }
    if (!file) { toast.error("Please pick a file (PDF / image)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({
        vendor: form.vendor, amount: String(Number(form.amount) || 0), date: form.date, notes: form.notes,
      });
      await fapi.post(`/finance/bills?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bill uploaded");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="upload-bill-modal">
      <div className="brut-card w-full max-w-lg bg-white">
        <div className="flex items-center justify-between p-5 border-b-2 border-black bg-[#0EA5E9] text-white">
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-80">UPLOAD · BILL</div>
            <h2 className="font-display font-black text-2xl">New Bill / Invoice</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/20"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Vendor *</div>
            <input className="brut-input" value={form.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder="Dutees, Behraji…" data-testid="bill-vendor-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Amount (₹) *</div>
              <input type="number" min={0} step="0.01" className="brut-input font-mono-ds font-bold text-lg" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="bill-amount-input" />
            </div>
            <div>
              <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Date</div>
              <input type="date" className="brut-input font-mono-ds" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="bill-date-input" />
            </div>
          </div>
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">File (PDF / JPG / PNG · ≤8 MB) *</div>
            <input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm file:mr-3 file:border-2 file:border-black file:px-4 file:py-2 file:bg-white file:font-mono-ds file:font-bold file:uppercase file:text-[10px] file:cursor-pointer" data-testid="bill-file-input" />
            {file && (
              <div className="font-mono-ds text-[10px] uppercase text-zinc-500 mt-1">
                Picked: {file.name} · {Math.round(file.size / 1024)} KB
              </div>
            )}
          </div>
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Notes</div>
            <textarea rows={2} className="brut-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="bill-notes-input" />
          </div>
        </div>
        <div className="border-t-2 border-black p-5 flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-5 py-2.5">Cancel</button>
          <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5 disabled:opacity-60" data-testid="save-bill-btn">
            {busy ? "Uploading…" : "Upload Bill"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`brut-card p-4 ${accent ? "bg-black text-white" : ""}`}>
      <div className={`font-mono-ds text-[10px] tracking-widest uppercase mb-1 ${accent ? "opacity-70" : "text-zinc-500"}`}>{label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
}

function EntryForm({ entry, category, onClose, onSaved, fapi }) {
  const isEdit = !!entry;
  const catMeta = CATEGORIES.find((c) => c.id === (entry?.category || category));
  const [form, setForm] = useState({
    category: entry?.category || category,
    subcategory: entry?.subcategory || "",
    amount: entry?.amount ?? "",
    date: entry?.date || todayIST(),
    notes: entry?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!form.subcategory.trim()) { toast.error("Sub-category is required"); return; }
    if (form.amount === "" || Number(form.amount) < 0) { toast.error("Amount must be 0 or greater"); return; }
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (isEdit) await fapi.put(`/finance/${entry.id}`, payload);
      else await fapi.post("/finance", payload);
      toast.success(isEdit ? "Entry updated" : "Entry added");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  const activeCatMeta = CATEGORIES.find((c) => c.id === form.category);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="finance-form-modal">
      <div className="brut-card w-full max-w-2xl bg-white">
        <div className={`flex items-center justify-between p-5 border-b-2 border-black ${activeCatMeta.accent} text-white`}>
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-80">{isEdit ? "EDIT" : "NEW"} · {activeCatMeta.label}</div>
            <h2 className="font-display font-black text-2xl">{isEdit ? "Edit Entry" : "New Finance Entry"}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/20" data-testid="close-finance-form"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-2">Category</div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button key={c.id} type="button" onClick={() => set("category", c.id)} data-testid={`finance-form-cat-${c.id}`}
                  className={`px-3 py-2 border-2 border-black font-mono-ds text-xs font-bold tracking-wider transition-all ${
                    form.category === c.id ? `${c.accent} text-white shadow-[3px_3px_0_0_#0a0a0a]` : "bg-white hover:bg-zinc-100"
                  }`}>{c.short}</button>
              ))}
            </div>
          </div>

          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-2">Sub-category (pick or type)</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {activeCatMeta.presets.map((p) => (
                <button key={p} type="button" onClick={() => set("subcategory", p)} data-testid={`finance-preset-${p}`}
                  className={`px-2.5 py-1 border-2 border-black font-mono-ds text-[10px] font-bold uppercase transition-all ${
                    form.subcategory === p ? "bg-black text-white" : "bg-white hover:bg-zinc-100"
                  }`}>{p}</button>
              ))}
            </div>
            <input className="brut-input" value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} placeholder="e.g. Rent · Meta Ads · Custom name" data-testid="finance-subcat-input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Amount (₹)</div>
              <input type="number" min={0} step="0.01" className="brut-input font-mono-ds font-bold text-lg" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="finance-amount-input" placeholder="0" />
            </div>
            <div>
              <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Date</div>
              <input type="date" className="brut-input font-mono-ds" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="finance-date-input" />
            </div>
          </div>

          <div>
            <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">Notes</div>
            <textarea rows={2} className="brut-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="finance-notes-input" placeholder="Optional details…" />
          </div>
        </div>
        <div className="border-t-2 border-black p-5 flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-5 py-2.5" data-testid="cancel-finance-btn">Cancel</button>
          <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5 disabled:opacity-60" data-testid="save-finance-btn">
            {busy ? "Saving…" : (isEdit ? "Save Changes" : "Save Entry")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChangePinModal({ onClose, fapi }) {
  const [current, setCurrent] = useState("");
  const [nxt, setNxt] = useState("");
  const [nxt2, setNxt2] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (nxt.length < 4 || nxt.length > 8 || !/^\d+$/.test(nxt)) { toast.error("New PIN must be 4–8 digits"); return; }
    if (nxt !== nxt2) { toast.error("PINs do not match"); return; }
    setBusy(true);
    try {
      await fapi.post("/finance/pin", { current_pin: current, new_pin: nxt });
      toast.success("PIN updated");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" data-testid="change-pin-modal">
      <div className="brut-card w-full max-w-md bg-white">
        <div className="flex items-center justify-between p-5 border-b-2 border-black bg-black text-white">
          <h2 className="font-display font-black text-xl inline-flex items-center gap-2"><KeyRound size={18} /> Change Finance PIN</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Input label="Current PIN" val={current} set={setCurrent} test="cur-pin-input" />
          <Input label="New PIN (4–8 digits)" val={nxt} set={setNxt} test="new-pin-input" />
          <Input label="Confirm New PIN" val={nxt2} set={setNxt2} test="new-pin2-input" />
        </div>
        <div className="border-t-2 border-black p-5 flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-5 py-2.5">Cancel</button>
          <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5 disabled:opacity-60" data-testid="save-pin-btn">
            {busy ? "Updating…" : "Update PIN"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, val, set, test }) {
  const [visible, setVisible] = useState(false);
  return (
    <label className="block">
      <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">{label}</div>
      <div className="relative">
        <input type={visible ? "text" : "password"} inputMode="numeric" pattern="\d*" maxLength={8}
          value={val} onChange={(e) => set(e.target.value.replace(/[^0-9]/g, ""))}
          className="brut-input font-mono-ds text-lg tracking-widest pr-10" data-testid={test} />
        <button type="button" onClick={() => setVisible((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 border-2 border-[#2F343A] bg-white hover:bg-zinc-100"
          aria-label={visible ? "Hide" : "Show"}
          data-testid={test ? `${test}-toggle` : undefined}
        >
          {visible ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </label>
  );
}
